import {
  notificationsEnabledQuery, activeNotificationsQuery, closedNotificationsQuery,
  notificationStatesQuery, completeNotificationMutation,
} from '@msales/shared/graphql/notifications';
import { transact, applyStates, mergeSnapshot, pruneHistory } from './store';

export class NotificationService {
  constructor(client, scope, allowed, expectedUid, progress = {}) {
    this.client = client; this.scope = scope; this.allowed = allowed; this.expectedUid = expectedUid;
    this.disposed = false; this.enabled = null; this.running = null;
    this.owner = `${Date.now()}-${Math.random()}`;
    this.controllers = new Set();
    this.progress = progress;
    this.progressActive = false;
    this.queuedSend = false;
    this.runningSends = false;
  }
  dispose() { this.disposed = true; this.controllers.forEach(c => c.abort()); }
  check() { if (this.disposed || !this.allowed()) throw new Error('Sinhronizacija je pauzirana'); }
  async request(document, variables = {}, mutation = false) {
    this.check();
    const controller = new AbortController(); this.controllers.add(controller);
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error('Sinhronizacija obavijesti je istekla. Pokušajte ponovo.'));
      }, 30000);
    });
    try {
      const options = { variables, context: { notificationExpectedUid: this.expectedUid, fetchOptions: { signal: controller.signal } } };
      const response = await Promise.race([timeout, mutation
        ? this.client.mutate({ ...options, mutation: document })
        : this.client.query({ ...options, query: document, fetchPolicy: 'no-cache', errorPolicy: 'none' })]);
      this.check();
      if (response.errors?.length || !response.data) throw new Error('Nepotpun odgovor servera');
      return response.data;
    } finally { clearTimeout(timer); this.controllers.delete(controller); }
  }
  async capability() {
    if (this.enabled !== null) return this.enabled;
    try { this.enabled = Boolean((await this.request(notificationsEnabledQuery)).notificationsEnabled); }
    catch (error) {
      const errors = [...(error.graphQLErrors || []), ...(error.networkError?.result?.errors || [])];
      if (errors.some(e => /Cannot query field.*notificationsEnabled/.test(e.message))) this.enabled = false;
      else throw error;
    }
    return this.enabled;
  }
  async states(ids) {
    if (ids.some(id => !/^\d{1,38}$/.test(String(id)))) throw new Error('Neispravan ID obavijesti');
    const results = [];
    for (let i = 0; i < ids.length; i += 100) {
      const batch = ids.slice(i, i + 100);
      const response = (await this.request(notificationStatesQuery, { ids: batch })).notificationStates;
      if (!Array.isArray(response) || response.length !== batch.length || new Set(response.map(r => r.id)).size !== batch.length ||
          response.some(r => !batch.includes(r.id) || !['ACTIVE', 'COMPLETED', 'UNAVAILABLE'].includes(r.status) ||
            (r.status !== 'UNAVAILABLE' && (!r.notification || r.notification.id !== r.id)) ||
            (r.status === 'COMPLETED' && !r.notification.completedAt))) throw new Error('Nepotpuna provjera obavijesti');
      results.push(...response);
    }
    return results;
  }
  async send() {
    this.check();
    const locked = await transact(this.scope, state => {
      if (!state.lease || state.lease.until < Date.now()) state.lease = { owner: this.owner, until: Date.now() + 60000 };
    });
    if (locked.lease.owner !== this.owner) return;
    try {
      for (const id of Object.keys(locked.operations)) {
        this.check();
        const latest = await transact(this.scope, state => {
          if (state.lease?.owner === this.owner) state.lease.until = Date.now() + 60000;
        });
        if (latest.lease?.owner !== this.owner) return;
        const operation = latest.operations[id];
        if (!operation || operation.permanent) continue;
        try {
          const result = (await this.request(completeNotificationMutation, { id }, true)).completeNotification;
          if (!result || result.id !== id || !['COMPLETED', 'UNAVAILABLE'].includes(result.status) ||
              (result.status === 'COMPLETED' && !result.notification?.completedAt)) throw new Error('Zatvaranje nije potvrđeno');
          await transact(this.scope, state => { applyStates(state, [result]); pruneHistory(state); });
        } catch (error) {
          if (this.disposed) throw error;
          const codes = (error.graphQLErrors || []).map(e => e.extensions?.code);
          await transact(this.scope, state => {
            if (state.operations[id]) {
              state.operations[id].attemptCount += 1;
              state.operations[id].lastError = 'Zatvaranje nije poslano. Pokušajte ponovo.';
              state.operations[id].permanent = codes.includes('BAD_USER_INPUT');
            }
          });
          if (!codes.includes('BAD_USER_INPUT')) throw error;
        }
      }
    } finally {
      await transact(this.scope, state => { if (state.lease?.owner === this.owner) delete state.lease; });
    }
  }
  sync(send = false, showProgress = false) {
    if (showProgress && !this.progressActive && this.enabled !== false) {
      this.progressActive = true;
      if (this.progress.start) this.progress.start();
    }
    if (this.running) {
      // Multiple manual clicks join one send cycle. Upgrade a read-only cycle
      // once, rather than adding another full sync for every caller.
      if (send && !this.runningSends) this.queuedSend = true;
      return this.running;
    }
    this.runningSends = send;
    this.running = (async () => {
      let success = false;
      try {
        await this.run(send);
        if (this.queuedSend) {
          this.queuedSend = false;
          this.runningSends = true;
          await this.run(true);
        }
        success = true;
      } finally {
        this.running = null;
        this.queuedSend = false;
        this.runningSends = false;
        if (this.progressActive) {
          this.progressActive = false;
          if (this.progress.complete) this.progress.complete(success);
        }
      }
    })();
    return this.running;
  }
  async run(send) {
    const startedAt = Date.now();
    if (!(await this.capability())) return;
    // Fail promptly: a failed upload must not wait for another full download
    // before the user sees the error. The existing local list remains intact.
    if (send) await this.send();
    this.check();
    const before = await transact(this.scope);
    let nodes = [];
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        nodes = [];
        let snapshotToken = null; let cursor = null;
        const seen = new Set();
        do {
          const page = (await this.request(activeNotificationsQuery, { snapshotToken, cursor })).activeNotifications;
          if (!page || !Array.isArray(page.nodes) || !page.snapshotToken ||
              (snapshotToken && snapshotToken !== page.snapshotToken) || (!page.complete && (!page.cursor || seen.has(page.cursor)))) {
            throw new Error('Nepotpuna stranica obavijesti');
          }
          nodes.push(...page.nodes); snapshotToken = page.snapshotToken;
          cursor = page.complete ? null : page.cursor; seen.add(cursor);
        } while (cursor);
        break;
      } catch (error) {
        if (attempt || !(error.graphQLErrors || []).some(e => e.extensions?.code === 'SNAPSHOT_EXPIRED')) throw error;
      }
    }
    const unique = [...new Map(nodes.map(row => [row.id, row])).values()];
    const present = new Set(unique.map(row => row.id));
    const missing = Object.values(before.rows).filter(row => !row.completedAt && !present.has(row.id)).map(row => row.id);
    const results = await this.states(missing);
    this.check();
    await transact(this.scope, state => mergeSnapshot(state, unique, results, before.generation));
    console.info('[Notifications] sync complete', { durationMs: Date.now() - startedAt, count: unique.length });
    if (send && Object.values((await transact(this.scope)).operations).some(op => op.permanent)) throw new Error('Neka zatvaranja zahtijevaju provjeru');
  }
  async history(cursor) {
    this.check();
    const state = await transact(this.scope);
    if (!this.historyCheckedThisSession || !state.historyChecked || Date.now() - Date.parse(state.historyChecked) > 86400000) {
      const results = await this.states(Object.values(state.rows).filter(row => row.completedAt).map(row => row.id));
      await transact(this.scope, current => { applyStates(current, results); current.historyChecked = new Date().toISOString(); });
      this.historyCheckedThisSession = true;
    }
    const page = (await this.request(closedNotificationsQuery, { cursor })).closedNotifications;
    if (!page || !Array.isArray(page.nodes)) throw new Error('Nepotpuna historija');
    await transact(this.scope, current => { applyStates(current, page.nodes.map(notification => ({ id: notification.id, status: 'COMPLETED', notification }))); pruneHistory(current); });
    return page;
  }
  async inspect(id) {
    const results = await this.states([id]);
    await transact(this.scope, state => applyStates(state, results));
  }
  async retry(id, cancel = false) {
    if (cancel) {
      const results = await this.states([id]);
      await transact(this.scope, state => { applyStates(state, results); delete state.operations[id]; });
    } else await transact(this.scope, state => {
      if (state.operations[id]) { state.operations[id].permanent = false; state.operations[id].lastError = null; }
    });
  }
}
